import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react"
import { WingsPagination } from "@wingspans-ui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { faSearch } from "@fortawesome/pro-regular-svg-icons"
import { useQueryParam, JsonParam, StringParam } from "use-query-params"
import { FormattedMessage, useIntl } from "react-intl"
import useWeightedFuseSearch from "../hooks/useWeightedFuseSearch"

const DEFAULT_SEARCH_OPTIONS = {
  threshold: 0.35,
  ignoreLocation: true,
}

const normalizeText = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeText(item)).join(" ")
  }

  if (value && typeof value === "object") {
    return normalizeText(Object.values(value))
  }

  if (value === null || value === undefined) {
    return ""
  }

  return String(value).toLowerCase().trim()
}

const getValueByPath = (item, keyPath) => {
  if (!item || !keyPath) {
    return ""
  }

  return keyPath.split(".").reduce((acc, key) => {
    if (acc === null || acc === undefined) {
      return ""
    }

    return acc[key]
  }, item)
}

const matchesQueryTokensForKeys = (item, query, searchKeys) => {
  const normalizedQuery = normalizeText(query)
  if (!normalizedQuery) {
    return true
  }

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean)
  if (!tokens.length) {
    return true
  }

  const searchableText = normalizeText((searchKeys || []).map((keyConfig) => {
    const keyPath = typeof keyConfig === "string" ? keyConfig : keyConfig?.name
    return getValueByPath(item, keyPath)
  }))

  return tokens.every((token) => searchableText.includes(token))
}

const parseSavedSlugs = (filterList) => {
  if (!filterList || filterList === "false_attribute:false") {
    return []
  }

  const matches = [...filterList.matchAll(/slug:([^\s)]+)/g)]
  return matches.map((match) => match[1])
}

const toDateValue = (value) => {
  if (!value) {
    return 0
  }

  const timestamp = Date.parse(value)
  return Number.isNaN(timestamp) ? 0 : timestamp
}

const getDefaultSearchState = () => ({
  query: "",
  page: 1,
})

const normalizeSearchState = (state) => {
  const defaultState = getDefaultSearchState()
  const normalizedPage = Math.max(1, Number(state?.page) || 1)

  return {
    ...defaultState,
    ...(state || {}),
    page: normalizedPage,
  }
}

const getDefaultRecencyDate = (item) => {
  return item?.created_at || item?.modified_at || item?.published_at
}

const getItemIdentity = (item) => {
  return item?.id || item?.objectID || item?.slug
}

const DEFAULT_SORT_OPTIONS = [
  { key: "NEWEST", labelId: "algolia-stories-view-newest", defaultMessage: "Newest" },
  { key: "SAVED", labelId: "algolia-stories-view-saved", defaultMessage: "Saved" },
]

/**
 * Isolated, memoized search input.
 *
 * Holds its own local value state so that rapid typing / deleting only re-renders
 * this small component. The (heavy) parent search + results update is driven by the
 * debounced onQueryChange callback, keeping the input itself lag-free.
 */
const SearchInput = React.memo(({ value, onQueryChange, onClear, placeholder, className = "" }) => {
  const [localValue, setLocalValue] = useState(value || "")
  const lastExternalValue = useRef(value || "")

  useEffect(() => {
    const nextValue = value || ""
    if (nextValue !== lastExternalValue.current) {
      lastExternalValue.current = nextValue
      setLocalValue(nextValue)
    }
  }, [value])

  const handleChange = (event) => {
    const nextValue = event.target.value
    lastExternalValue.current = nextValue
    setLocalValue(nextValue)
    onQueryChange(nextValue)
  }

  const handleClear = () => {
    lastExternalValue.current = ""
    setLocalValue("")
    onClear()
  }

  return (
    <div className={`ais-SearchBox ${className}`.trim()}>
      <form className="ais-SearchBox-form" onSubmit={(event) => event.preventDefault()}>
        <input
          className="ais-SearchBox-input"
          type="search"
          value={localValue}
          onChange={handleChange}
          placeholder={placeholder}
        />
        {localValue.length > 0 && (
          <button type="button" className="ais-SearchBox-reset" onClick={handleClear}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
        <button type="submit" className="ais-SearchBox-submit">
          <FontAwesomeIcon icon={faSearch} />
        </button>
      </form>
    </div>
  )
})

SearchInput.displayName = "SearchInput"

/**
 * Reusable local GraphQL-based explore list with search, saved/newest sort, and pagination.
 */
const LocalExplore = ({
  classContext,
  filterList,
  items,
  searchKeys,
  renderCard,
  hitsPerPage = 8,
  searchPlaceholder,
  preserveSearchRelevanceOrder = true,
  maxSearchMatchScore,
  minQueryLengthForScoreFilter,
  fallbackToUnfilteredOnEmpty,
  filterItemByQuery,
  fallbackToUnfilteredOnPostFilterEmpty = true,
  getQueryPriority,
  getRecencyDate = getDefaultRecencyDate,
  searchOptions = DEFAULT_SEARCH_OPTIONS,
  sortOptions = DEFAULT_SORT_OPTIONS,
  getItemsForSortMode,
  renderSortExtras,
}) => {
  const { formatMessage } = useIntl()
  const [queryFilters, setQueryFilters] = useQueryParam("queryFilters", JsonParam)
  const [sortByPageQuery, setSortByPageQuery] = useQueryParam("sortByPageQuery", StringParam)

  const [sortByPage, setSortByPage] = useState(sortByPageQuery || "NEWEST")
  const [searchState, setSearchState] = useState(normalizeSearchState(queryFilters))
  const deferredQuery = useDeferredValue(searchState.query)

  const effectiveFilterItemByQuery = useMemo(() => {
    if (typeof filterItemByQuery === "function") {
      return filterItemByQuery
    }

    return (item, query) => matchesQueryTokensForKeys(item, query, searchKeys)
  }, [filterItemByQuery, searchKeys])

  const debouncedSetState = useRef(null)
  const savedSlugs = useMemo(() => parseSavedSlugs(filterList), [filterList])

  useEffect(() => {
    setSortByPage(sortByPageQuery || "NEWEST")
  }, [sortByPageQuery])

  useEffect(() => {
    const isValidSortOption = sortOptions.some((option) => option.key === sortByPage)
    if (!isValidSortOption) {
      setSortByPage("NEWEST")
      setSortByPageQuery("NEWEST")
    }
  }, [sortByPage, sortOptions, setSortByPageQuery])

  useEffect(() => {
    setSearchState(normalizeSearchState(queryFilters))
  }, [queryFilters])

  useEffect(() => {
    return () => {
      clearTimeout(debouncedSetState.current)
    }
  }, [])

  const sortedItems = useMemo(() => {
    const recencySortedItems = [...items].sort((a, b) => {
      return toDateValue(getRecencyDate(b)) - toDateValue(getRecencyDate(a))
    })

    if (sortByPage === "SAVED") {
      const savedSet = new Set(savedSlugs)
      return recencySortedItems.filter((item) => savedSet.has(item.slug))
    }

    if (sortByPage !== "NEWEST" && typeof getItemsForSortMode === "function") {
      return getItemsForSortMode(recencySortedItems, sortByPage)
    }

    return recencySortedItems
  }, [items, sortByPage, savedSlugs, getRecencyDate, getItemsForSortMode])

  const itemsByQuery = useWeightedFuseSearch({
    items: sortedItems,
    query: deferredQuery,
    keys: searchKeys,
    minQueryLength: 2,
    fuseOptions: searchOptions,
    maxMatchScore: maxSearchMatchScore,
    minQueryLengthForScoreFilter,
    fallbackToUnfilteredOnEmpty,
  })

  const postFilteredItemsByQuery = useMemo(() => {
    const normalizedQuery = (deferredQuery || "").trim()

    if (!normalizedQuery) {
      return itemsByQuery
    }

    const filteredItems = itemsByQuery.filter((item) => effectiveFilterItemByQuery(item, normalizedQuery))

    const matchedItems = filteredItems.length === 0 && fallbackToUnfilteredOnPostFilterEmpty
      ? itemsByQuery
      : filteredItems

    if (typeof getQueryPriority !== "function") {
      return matchedItems
    }

    const matchedItemIdentities = new Set(matchedItems.map((item) => getItemIdentity(item)))
    const additionalPriorityMatches = sortedItems.filter((item) => {
      const identity = getItemIdentity(item)
      if (matchedItemIdentities.has(identity)) {
        return false
      }

      return (getQueryPriority(item, normalizedQuery) || 0) > 0
    })

    const prioritizedItems = [...matchedItems, ...additionalPriorityMatches]

    return prioritizedItems
      .map((item, index) => ({ item, index, priority: getQueryPriority(item, normalizedQuery) || 0 }))
      .sort((a, b) => (b.priority - a.priority) || (a.index - b.index))
      .map((entry) => entry.item)
  }, [
    itemsByQuery,
    sortedItems,
    deferredQuery,
    effectiveFilterItemByQuery,
    fallbackToUnfilteredOnPostFilterEmpty,
    getQueryPriority,
  ])

  const visibleItems = useMemo(() => {
    const hasQuery = (deferredQuery || "").trim().length > 0
    const isCustomSortMode = sortByPage !== "NEWEST"

    // Custom sort modes (e.g. MATCHES) already produce their own ordering via
    // getItemsForSortMode; re-sorting by recency here would discard that order.
    if ((preserveSearchRelevanceOrder && hasQuery) || isCustomSortMode) {
      return postFilteredItemsByQuery
    }

    return [...postFilteredItemsByQuery].sort((a, b) => {
      return toDateValue(getRecencyDate(b)) - toDateValue(getRecencyDate(a))
    })
  }, [postFilteredItemsByQuery, getRecencyDate, preserveSearchRelevanceOrder, deferredQuery, sortByPage])

  const totalPages = Math.max(1, Math.ceil(visibleItems.length / hitsPerPage))
  const currentPage = Math.min(searchState.page || 1, totalPages)

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * hitsPerPage
    return visibleItems.slice(startIndex, startIndex + hitsPerPage)
  }, [visibleItems, currentPage, hitsPerPage])

  const updateSearchState = (nextState, debounceQuery = false) => {
    setSearchState(nextState)

    clearTimeout(debouncedSetState.current)

    if (debounceQuery) {
      debouncedSetState.current = setTimeout(() => {
        setQueryFilters(nextState)
      }, 1000)
      return
    }

    setQueryFilters(nextState)
  }

  const handleInputQueryChange = useCallback((nextQuery) => {
    clearTimeout(debouncedSetState.current)
    debouncedSetState.current = setTimeout(() => {
      const nextState = { query: nextQuery, page: 1 }
      setSearchState(nextState)
      setQueryFilters(nextState)
    }, 250)
  }, [setQueryFilters])

  const handleClearQuery = useCallback(() => {
    clearTimeout(debouncedSetState.current)
    const nextState = { query: "", page: 1 }
    setSearchState(nextState)
    setQueryFilters(nextState)
  }, [setQueryFilters])

  const searchInputPlaceholder = searchPlaceholder ?? formatMessage({
    id: "algolia-stories-view-search-placeholder",
    defaultMessage: "Search by name, job title, location, company",
  })

  const handleSortByClick = (event, page) => {
    event.preventDefault()
    if (page === sortByPage) {
      return
    }

    setSortByPage(page)
    setSortByPageQuery(page)
    updateSearchState({
      query: "",
      page: 1,
    })
  }

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) {
      return
    }

    updateSearchState({
      ...searchState,
      page: nextPage,
    })

    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  return (
    <>
      <SearchInput
        value={searchState.query || ""}
        onQueryChange={handleInputQueryChange}
        onClear={handleClearQuery}
        placeholder={searchInputPlaceholder}
        className="explore-search-desktop"
      />

      <div className="ais-ScrollTo">
        <div className={`w-default-algolia-card-hits-container stream stream-${classContext} stream-${classContext}-explore`}>
          <div className="stream-explore-filters">
            <ul className="explore-filters-list">
              <li className="explore-filters-list-item explore-filters-list-item-sort">
                <FormattedMessage id="algolia-stories-view-sort-by" defaultMessage="Sort by:" />
              </li>

              {sortOptions.map((option) => {
                const itemClass = option.key === "SAVED"
                  ? "explore-filters-list-item-saved"
                  : "explore-filters-list-item-newest"

                return (
                  <li key={option.key} className={`explore-filters-list-item ${itemClass}`}>
                    <a href="/" className={sortByPage === option.key ? "active" : ""} onClick={(event) => handleSortByClick(event, option.key)}>
                      <FormattedMessage id={option.labelId} defaultMessage={option.defaultMessage} />
                    </a>
                  </li>
                )
              })}
            </ul>

            {typeof renderSortExtras === "function" && renderSortExtras({
              sortByPage,
              totalItems: visibleItems.length,
            })}
          </div>

          <SearchInput
            value={searchState.query || ""}
            onQueryChange={handleInputQueryChange}
            onClear={handleClearQuery}
            placeholder={searchInputPlaceholder}
            className="explore-search-mobile"
          />

          <div className={`ais-Hits ${paginatedItems.length === 0 ? "ais-Hits-empty" : ""}`.trim()}>
            <ul className="ais-Hits-list">
              {paginatedItems.map((item) => (
                <li key={item.id || item.objectID || item.slug} className="ais-Hits-item">
                  {renderCard(item, { sortByPage })}
                </li>
              ))}
            </ul>

            {paginatedItems.length === 0 && (
              <p className="ais-Hits-empty-message">
                <FormattedMessage id="algolia-empty-state" defaultMessage="No results found." />
              </p>
            )}
          </div>

          <WingsPagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      </div>
    </>
  )
}

export default LocalExplore
